import { useState } from 'react';
import type { Feuille } from '../partage/api/types';
import { PastilleEtat } from '../apparence';
import { aujourdhuiEnLettres, jourEtMoisAbrege } from '../partage/datesEnMots';
import {
  AUJ,
  compteDeLEtape,
  faitesSurN,
  FRISE_REPLIER,
  MARGE_AVANCEMENT,
  MARGE_FRISE,
  MARGE_REPERTOIRE,
  PERSONNE_DANS_LE_PROJET,
  plusLoinDansLaFrise,
  RIEN_A_AVANCER,
  RIEN_DE_DATE_A_VENIR,
} from '../partage/vocabulaire';
import { avancementDeLaFeuille, friseDeLaFeuille, repertoireDeLaFeuille } from './contenuDesMarges';
import type { Marge } from './modes';
import './MargesDeLaCarte.css';

/**
 * **Les marges M, R, F de la carte** (chantier modes-de-la-carte, modification 03 ; maquette
 * `quatre-themes.html`, `margesExecution`, `.marge`, `.mg`, `.md`, `.mh`) : posées sur la scène,
 * hors du monde de la carte, qui se pousse pour leur faire place (`feuille-contenu--marge-*`).
 *
 * - M, à gauche, « Comment j'avance » : chaque étape, son état, son compte (« 2/5 »), barrée si
 *   faite ; en bas, le compte de la feuille ;
 * - R, à droite, le répertoire : chaque contact, son numéro, ses bulles ; un clic l'allume sur la
 *   carte, un autre l'éteint ;
 * - F, en haut, la frise des dates : aujourd'hui, puis chaque bulle datée, à sa place.
 *
 * Plusieurs à la fois ; la frise ouverte, les deux autres commencent sous elle.
 */
export function MargesDeLaCarte({
  feuille,
  marges,
  contactChoisi,
  onContact,
}: {
  feuille: Feuille;
  marges: ReadonlySet<Marge>;
  contactChoisi: string | null;
  onContact: (cle: string, bulles: readonly string[]) => void;
}) {
  const sousLaFrise = marges.has('frise') ? ' marge--sous-frise' : '';
  return (
    <>
      {marges.has('avancement') && <MargeDeLAvancement feuille={feuille} classe={sousLaFrise} />}
      {marges.has('repertoire') && (
        <MargeDuRepertoire feuille={feuille} classe={sousLaFrise} choisi={contactChoisi} onContact={onContact} />
      )}
      {marges.has('frise') && <MargeDeLaFrise feuille={feuille} />}
    </>
  );
}

function MargeDeLAvancement({ feuille, classe }: { feuille: Feuille; classe: string }) {
  const { etapes, decompte } = avancementDeLaFeuille(feuille);
  return (
    <aside className={`marge marge-g${classe}`} aria-label={MARGE_AVANCEMENT} data-marge="avancement">
      <h4>{MARGE_AVANCEMENT}</h4>
      {etapes.length === 0 ? (
        <p className="marge-vide">{RIEN_A_AVANCER}</p>
      ) : (
        <div className="marge-regle">
          {etapes.map((e) => (
            <div key={e.id} className="marge-cran" data-bulle={e.id}>
              <PastilleEtat etat={e.etat} taille={13} />
              <span className={e.faite ? 'marge-barre' : ''}>{e.titre}</span>
              {e.decompte.total > 0 && <em>{compteDeLEtape(e.decompte.faites, e.decompte.total)}</em>}
            </div>
          ))}
        </div>
      )}
      <p className="marge-bilan">{faitesSurN(decompte.faites, decompte.total)}</p>
    </aside>
  );
}

function MargeDuRepertoire({
  feuille,
  classe,
  choisi,
  onContact,
}: {
  feuille: Feuille;
  classe: string;
  choisi: string | null;
  onContact: (cle: string, bulles: readonly string[]) => void;
}) {
  const contacts = repertoireDeLaFeuille(feuille);
  return (
    <aside className={`marge marge-d${classe}`} aria-label={MARGE_REPERTOIRE} data-marge="repertoire">
      <h4>{MARGE_REPERTOIRE}</h4>
      {contacts.length === 0 && <p className="marge-vide">{PERSONNE_DANS_LE_PROJET}</p>}
      {contacts.map((c) => (
        <button
          key={c.cle}
          type="button"
          className="marge-contact"
          aria-pressed={c.cle === choisi}
          onClick={() =>
            onContact(
              c.cle,
              c.bulles.map((b) => b.id)
            )
          }
        >
          {c.nom && <b>{c.nom}</b>}
          {c.numero && <span className="marge-numero">{c.numero}</span>}
          <em>{c.bulles.map((b) => b.titre).join(', ')}</em>
        </button>
      ))}
    </aside>
  );
}

/** La frise montre les quatre prochaines semaines ; « + N plus loin » l'étend à tout (retour d'Igor du 2026-10-22). */
const HORIZON_DE_LA_FRISE = 28;

function MargeDeLaFrise({ feuille }: { feuille: Feuille }) {
  const [toutes, setToutes] = useState(false);
  const { crans, plusLoin } = friseDeLaFeuille(feuille, undefined, toutes ? null : HORIZON_DE_LA_FRISE);
  return (
    <aside className="marge marge-h" aria-label={MARGE_FRISE} data-marge="frise">
      <div className="marge-frise">
        <span className="marge-jour" style={{ left: '2%' }}>
          <i className="marge-point marge-point--auj" />
          <b>{AUJ}</b>
          {aujourdhuiEnLettres(true)}
        </span>
        {crans.map((c, i) => (
          <span
            key={c.id}
            className={`marge-jour${i % 2 ? ' marge-jour--decale' : ''}`}
            style={{ left: `${4 + c.place * 92}%` }}
            data-bulle={c.id}
          >
            <i className={`marge-point${c.faite ? ' marge-point--fait' : c.proche ? ' marge-point--proche' : ''}`} />
            <b>{jourEtMoisAbrege(c.jour)}</b>
            {c.titre}
          </span>
        ))}
        {crans.length === 0 && <p className="marge-vide marge-vide--frise">{RIEN_DE_DATE_A_VENIR}</p>}
      </div>
      {(plusLoin > 0 || toutes) && (
        <button type="button" className="marge-frise-plus" onClick={() => setToutes(!toutes)}>
          {toutes ? FRISE_REPLIER : plusLoinDansLaFrise(plusLoin)}
        </button>
      )}
    </aside>
  );
}
